import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Plus, ToggleLeft, ToggleRight, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { TEMPLATES } from "@/lib/applicationTemplates";

const emptyForm = {
  name: "",
  description: "",
  responsibilities: "",
  requirements: "",
  is_open: true,
  max_openings: "",
  display_order: 0,
  questions_template: "moderation",
};

/**
 * OWNER-ONLY page — create / edit / open-close / delete positions.
 * Backend route uses get_owner_user which STRICTLY excludes admins.
 */
export default function OwnerPositions() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // position doc or "new"
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && (!user || user.role !== "owner")) navigate("/");
  }, [loading, user, navigate]);

  const load = async () => {
    const { data } = await api.get("/owner/positions");
    setItems(data.items || []);
  };
  useEffect(() => {
    if (user?.role === "owner") load();
  }, [user]);

  const openNew = () => { setEditing("new"); setForm(emptyForm); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      description: p.description || "",
      responsibilities: (p.responsibilities || []).join("\n"),
      requirements: (p.requirements || []).join("\n"),
      is_open: !!p.is_open,
      max_openings: p.max_openings ?? "",
      display_order: p.display_order ?? 0,
      questions_template: p.questions_template || "moderation",
    });
  };

  const save = async () => {
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      responsibilities: form.responsibilities.split("\n").map((s) => s.trim()).filter(Boolean),
      requirements: form.requirements.split("\n").map((s) => s.trim()).filter(Boolean),
      is_open: !!form.is_open,
      max_openings: form.max_openings === "" ? null : Number(form.max_openings) || null,
      display_order: Number(form.display_order) || 0,
      questions_template: form.questions_template || "moderation",
    };
    if (!payload.name || payload.name.length < 2) {
      toast.error("Name is required");
      return;
    }
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/owner/positions", payload);
        toast.success("Position created");
      } else {
        await api.put(`/owner/positions/${editing.id}`, payload);
        toast.success("Position updated");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleOpen = async (p) => {
    try {
      await api.put(`/owner/positions/${p.id}`, {
        name: p.name,
        description: p.description,
        responsibilities: p.responsibilities || [],
        requirements: p.requirements || [],
        is_open: !p.is_open,
        max_openings: p.max_openings ?? null,
        display_order: p.display_order ?? 0,
        questions_template: p.questions_template || "moderation",
      });
      toast.success(!p.is_open ? "Position opened" : "Position closed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (p) => {
    if (!confirm(`Delete "${p.name}"? Existing applications will remain but the position row is removed.`)) return;
    try {
      await api.delete(`/owner/positions/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  if (loading || !user) return <div className="p-8 text-zinc-500">Loading…</div>;
  if (user.role !== "owner") return <div className="p-8 text-red-400">Owner only.</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-24 pb-16" data-testid="owner-positions-page">
      <SEO title="Manage Positions · Serverlistr" canonical="/owner/positions" noindex />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-3xl font-black">Manage positions</h1>
        <button onClick={openNew} data-testid="new-position-btn" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-blurple hover:bg-blurple-hover text-white font-semibold">
          <Plus className="w-4 h-4" /> New position
        </button>
      </div>

      {items.length === 0 && (
        <p className="mt-6 text-zinc-500">No positions yet. Create your first one to start accepting applications.</p>
      )}

      <div className="mt-6 space-y-3">
        {items.map((p) => (
          <div key={p.id} data-testid="position-row" className="rounded-xl border border-white/10 bg-[#0A0A0F] p-4 flex items-center gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{p.name}</span>
                {p.is_open ? (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/40">OPEN</span>
                ) : (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-zinc-400 bg-white/[0.03] border border-white/10">CLOSED</span>
                )}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">{p.description}</div>
            </div>
            <button onClick={() => toggleOpen(p)} data-testid="toggle-open-btn" title={p.is_open ? "Close" : "Open"} className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-semibold">
              {p.is_open ? <ToggleRight className="w-5 h-5 text-emerald-400" /> : <ToggleLeft className="w-5 h-5 text-zinc-400" />}
            </button>
            <button onClick={() => openEdit(p)} data-testid="edit-position-btn" className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-semibold">
              <Pencil className="w-4 h-4" />
            </button>
            <button onClick={() => remove(p)} data-testid="delete-position-btn" className="h-9 px-3 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 text-sm font-semibold">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl bg-[#0A0A0F] border border-white/10 p-6 max-h-[85vh] overflow-y-auto" data-testid="position-editor">
            <h2 className="font-heading text-xl font-black">{editing === "new" ? "New position" : `Edit — ${editing.name}`}</h2>
            <div className="mt-4 space-y-3">
              <FormField label="Name" testId="pos-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <FormArea label="Description" testId="pos-description-input" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
              <FormArea label="Responsibilities (one per line)" testId="pos-responsibilities-input" value={form.responsibilities} onChange={(v) => setForm({ ...form, responsibilities: v })} rows={4} />
              <FormArea label="Requirements (one per line)" testId="pos-requirements-input" value={form.requirements} onChange={(v) => setForm({ ...form, requirements: v })} rows={4} />
              <div>
                <label className="block text-xs font-semibold text-zinc-400 mb-1">Application question template</label>
                <select
                  value={form.questions_template}
                  onChange={(e) => setForm({ ...form, questions_template: e.target.value })}
                  data-testid="pos-questions-template"
                  className="w-full h-11 px-3 bg-[#12121A] border border-white/10 rounded-lg text-sm focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple"
                >
                  {Object.values(TEMPLATES).map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
                <div className="mt-1 text-[11px] text-zinc-500">
                  {TEMPLATES[form.questions_template]?.description}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Max openings (optional)" testId="pos-max-openings-input" value={form.max_openings} onChange={(v) => setForm({ ...form, max_openings: v })} placeholder="e.g. 3" />
                <FormField label="Display order" testId="pos-display-order-input" value={form.display_order} onChange={(v) => setForm({ ...form, display_order: v })} placeholder="0" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_open} onChange={(e) => setForm({ ...form, is_open: e.target.checked })} data-testid="is-open-toggle" className="w-4 h-4 accent-blurple" />
                Currently accepting applications
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="h-10 px-4 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-semibold">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="save-position-btn" className="h-10 px-4 rounded-lg bg-blurple hover:bg-blurple-hover text-white font-bold disabled:opacity-50">
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-zinc-400 mb-1">{label}</div>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} className="w-full h-11 px-3 bg-[#12121A] border border-white/10 rounded-lg text-sm focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple" />
    </label>
  );
}
function FormArea({ label, value, onChange, rows = 3, testId }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-zinc-400 mb-1">{label}</div>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testId} className="w-full px-3 py-2 bg-[#12121A] border border-white/10 rounded-lg text-sm focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple resize-y" />
    </label>
  );
}
